import React from "react";
import { LucideIcon } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";

export interface FixedPageHeaderProps {
  title: string;
  description?: string;
  icon: LucideIcon;
  iconBgColor?: string;
  gradient?: string;
  actions?: React.ReactNode;
  avatar?: {
    src?: string;
    fallback: string;
  };
  userInfo?: {
    username?: string;
    role?: string;
  };
}

export function FixedPageHeader({
  title,
  description,
  icon: Icon,
  iconBgColor = "bg-icon-background",
  gradient = "from-header-primary to-header-primary",
  actions,
  avatar,
  userInfo,
}: FixedPageHeaderProps) {
  const { state } = useSidebar();
  const isMobile = useIsMobile();
  
  // Calculate left offset based on sidebar state
  const getLeftOffset = () => {
    if (isMobile) return "0px";
    return state === "collapsed" ? "var(--sidebar-width-icon)" : "var(--sidebar-width)";
  };

  // Determine text color class based on gradient
  const getTextColorClass = () => {
    if (gradient.includes('header-primary')) return 'text-header-primary-text';
    if (gradient.includes('header-yellow')) return 'text-header-yellow-text';
    if (gradient.includes('header-purple')) return 'text-header-purple-text';
    if (gradient.includes('header-pink')) return 'text-header-pink-text';
    return 'text-header-primary-text';
  };

  const textColorClass = getTextColorClass();

  return (
    <div 
      className="brand-page-header fixed top-0 z-40 h-[5.625rem] md:h-[7.5rem] bg-surface-background border-b border-border shadow-sm transition-all duration-300 ease-in-out"
      style={{
        left: getLeftOffset(),
        right: 0,
        width: `calc(100% - ${getLeftOffset()})`,
      }}
    >
      <div className="h-full px-4 md:px-6 flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-0">
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 flex-1">
          <SidebarTrigger className="mr-2 md:mr-4" />
          {avatar && (
            <Avatar className="hidden md:flex h-12 w-12 flex-shrink-0">
              <AvatarImage src={avatar.src} />
              <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
                {avatar.fallback}
              </AvatarFallback>
            </Avatar>
          )}
          {!avatar && (
            <div className={`brand-header-icon hidden md:block p-3 rounded-lg flex-shrink-0 ${iconBgColor}`}>
              <Icon className="h-7 w-7 text-icon-foreground" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-col md:flex-row items-start md:items-baseline gap-1 md:gap-3">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground leading-tight truncate">
                {title}
              </h1>
              {description && (
                <p className="hidden sm:block text-muted-foreground text-sm leading-tight">
                  {description}
                </p>
              )}
            </div>
          </div>
          {userInfo && (
            <div className="hidden md:block text-right">
              {userInfo.username && (
                <p className="text-muted-foreground text-xs leading-none">@{userInfo.username}</p>
              )}
              {userInfo.role && (
                <p className="text-muted-foreground text-xs uppercase tracking-wide leading-none">
                  {userInfo.role}
                </p>
              )}
            </div>
          )}
        </div>
        {actions && <div className="hidden md:flex items-center gap-2 flex-shrink-0">{actions}</div>}
      </div>
      <span data-tour="dashboard-header-center" aria-hidden="true" className="pointer-events-none absolute top-1/2 left-1/2 h-px w-px -translate-x-1/2 -translate-y-1/2 opacity-0" />
    </div>
  );
}